<template>
  <div>
    <TopBar currentTitle="Meal Plans">
      <b-button type="is-primary" size="is-small" icon-left="plus" @click="createNewMealPlan">Add Meal Plan</b-button>
    </TopBar>
    <div class="page-content">
    <h1 class="page-title">Meal Plans</h1>
    <p class="page-subtitle mb-4">
      <template v-if="hasActiveFilter">{{ filteredMealplans.length }} of {{ mealplans.length }} meal plans</template>
      <template v-else>{{ mealplans.length }} total</template>
    </p>

    <!-- Filters Bar -->
    <div class="filters-bar">
      <div class="search-wrapper">
        <b-icon icon="magnify" class="search-icon" />
        <b-input
          placeholder="Search meal plans..."
          v-model="query"
          @input="performSearch"
          expanded>
        </b-input>
      </div>
      <div class="filters-right">
        <a v-if="hasActiveFilter || query" class="clear-filters" @click="clearAll">Clear all</a>
        <b-select v-model="statusFilter" size="is-small" @input="onStatusFilterChange" :class="{ 'filter-active': statusFilter }">
          <option :value="''">All statuses</option>
          <option value="draft">Draft</option>
          <option value="test">Test</option>
          <option value="fix">Fix</option>
          <option value="live">Live</option>
        </b-select>
      </div>
    </div>

    <!-- Table Card -->
    <div class="feeds-card">
      <div class="feeds-card-body compact">
        <b-table
          :data="filteredMealplans"
          :hoverable="true"
          :mobile-cards="false"
          :paginated="true"
          :per-page="perPage"
          :current-page.sync="currentPage"
          :default-sort="[sortField, sortDirection]"
          @sort="onSort"
          pagination-position="bottom"
          @click="openMealPlan">

          <b-table-column width=48 label="Image" sortable v-slot="props">
            <figure class="m-0 p-0 image is-48x48">
              <img v-if="rowImageSrc(props.row) == null" class="thumbnail" src="@/assets/listing-placeholder.png">
              <img v-else class="thumbnail" :src="rowImageSrc(props.row)">
            </figure>
          </b-table-column>

          <b-table-column field="title" label="Title" sortable v-slot="props">
            <p class="meal-plan-title"
              :style="rowStyle(props.row)">
              {{ props.row.title }}
            </p>
          </b-table-column>

          <b-table-column field="status" width=100 label="Status" sortable centered v-slot="props">
            <FeedsStatusBadge :status="props.row.status" />
          </b-table-column>

          <b-table-column width=200 label="Recipes" v-slot="props">
            <div class="recipes-preview" @mouseenter="showRecipePanel($event, props.row)" @mouseleave="hideRecipePanel">
              <p v-for="(recipe) in getRecipes(props.row)" :key="recipe.id" class="recipe-name">
                • {{ recipe.title }}
              </p>
              <p v-if="getRecipes(props.row).length === 0" class="has-text-grey is-size-7">No recipes</p>
            </div>
          </b-table-column>

          <b-table-column field="date" width=140 label="Date" sortable centered v-slot="props">
            <span class="date-badge">
              {{ formatDate(props.row.date) }}
            </span>
          </b-table-column>

          <b-table-column v-if="isDesktop()" field="created_at" width=100 label="Created" sortable centered v-slot="props">
            <span class="date-tag">
              {{ new Date(props.row.created_at).toLocaleDateString() }}
            </span>
          </b-table-column>


          <b-table-column field="options" width=20 v-slot="props">
            <b-dropdown aria-role="list" position="is-bottom-left" @click.native.stop>
              <template #trigger>
                <b-button icon-right="dots-vertical" />
              </template>
              <b-dropdown-item aria-role="listitem" @click="duplicateMealPlan(props.row)">
                <b-icon size="is-small" icon="content-copy"></b-icon>
                Duplicate
              </b-dropdown-item>
              <b-dropdown-item class="has-text-danger" aria-role="listitem" @click="deleteMealPlan(props.row)">
                <b-icon size="is-small" icon="delete"></b-icon>
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered py-5">
              <p class="has-text-grey">No meal plans found</p>
            </div>
          </template>
        </b-table>
        <div class="pagination-footer">
          <div class="page-size-selector">
            <span class="page-size-label">Show:</span>
            <b-select v-model="perPage" size="is-small" @input="savePerPage">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </b-select>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Recipe hover panel -->
    <div
      v-if="hoveredMealPlan"
      class="recipe-hover-panel"
      :style="hoverPanelStyle"
      @mouseenter="keepRecipePanel"
      @mouseleave="hideRecipePanel">
      <div class="recipe-hover-header">Recipes</div>
      <div v-for="recipe in getRecipes(hoveredMealPlan)" :key="recipe.id" class="recipe-hover-item">
        <img
          v-if="recipeImageSrc(recipe)"
          :src="recipeImageSrc(recipe)"
          class="recipe-hover-image" />
        <img v-else class="recipe-hover-image" src="@/assets/listing-placeholder.png" />
        <span class="recipe-hover-title">{{ recipe.title }}</span>
      </div>
      <div v-if="getRecipes(hoveredMealPlan).length === 0" class="recipe-hover-empty">
        No recipes
      </div>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import DateFormatter from "@/formatters/DateFormatter"
import Colours from "@/models/Colours"
import debounce from 'lodash/debounce'
import search from '../search'
import FeedsStatusBadge from '@/components/common/FeedsStatusBadge.vue'
import TopBar from '@/components/layout/TopBar.vue'

const STORAGE_KEY = 'feeds_admin_mealplan_list_per_page'
const PAGE_STATE_KEY = 'feeds_list_page_MealPlanListVIew'
const SCROLL_STATE_KEY = 'feeds_list_state_MealPlanListVIew'
const SORT_STATE_KEY = 'feeds_list_sort_MealPlanListVIew'
const FILTER_STATE_KEY = 'feeds_list_filter_MealPlanListVIew'
const SEARCH_STATE_KEY = 'feeds_list_search_MealPlanListVIew'

export default {
  name: 'MealPlanListView',
  components: {
    FeedsStatusBadge,
    TopBar
  },
  data() {
    const savedSort = JSON.parse(sessionStorage.getItem(SORT_STATE_KEY) || 'null')
    return {
      mealplans: [],
      query: sessionStorage.getItem(SEARCH_STATE_KEY) || "",
      perPage: parseInt(localStorage.getItem(STORAGE_KEY)) || 25,
      currentPage: parseInt(sessionStorage.getItem(PAGE_STATE_KEY)) || 1,
      sortField: savedSort?.field || 'created_at',
      sortDirection: savedSort?.direction || 'desc',
      statusFilter: sessionStorage.getItem(FILTER_STATE_KEY) || '',
      scrollRestored: false,
      hoveredMealPlan: null,
      hoverPanelStyle: {},
      hoverTimeout: null
    }
  },
  watch: {
    currentPage(newPage) {
      sessionStorage.setItem(PAGE_STATE_KEY, newPage)
    },
    mealplans() {
      // Restore scroll position after data loads
      if (!this.scrollRestored) {
        this.scrollRestored = true
        this.$nextTick(() => {
          const savedState = sessionStorage.getItem(SCROLL_STATE_KEY)
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.scrollY > 0) {
              setTimeout(() => {
                window.scrollTo(state.scrollX, state.scrollY)
              }, 50)
            }
          }
        })
      }
    }
  },
  mounted() {
    if (this.query) {
      this.performSearch(this.query)
    }
  },
  firestore: {
    mealplans: db.collection('meal_plans').orderBy("created_at", "desc")
  },
  computed: {
    hasActiveFilter() {
      return !!this.statusFilter
    },
    filteredMealplans() {
      if (!this.statusFilter) return this.mealplans
      return this.mealplans.filter(m => m.status === this.statusFilter)
    }
  },
  methods: {
    getRecipes(mealplan) {
      const recipes = mealplan.recipes;
      if (!recipes || typeof recipes !== 'object') return [];
      return Object.keys(recipes).map(id => ({
        id,
        ...recipes[id]
      })).sort((a, b) => (a.position || 0) - (b.position || 0));
    },
    recipeImageSrc(recipe) {
      if (!recipe.images || !recipe.images.length) return null;
      return recipe.images[0].src || null;
    },
    showRecipePanel(event, mealplan) {
      clearTimeout(this.hoverTimeout);
      const rect = event.currentTarget.getBoundingClientRect();
      this.hoverPanelStyle = {
        top: `${rect.bottom + window.scrollY + 4}px`,
        left: `${rect.left + window.scrollX}px`
      };
      this.hoveredMealPlan = mealplan;
    },
    keepRecipePanel() {
      clearTimeout(this.hoverTimeout);
    },
    hideRecipePanel() {
      this.hoverTimeout = setTimeout(() => {
        this.hoveredMealPlan = null;
      }, 150);
    },
    savePerPage(value) {
      localStorage.setItem(STORAGE_KEY, value)
    },
    onSort(field, direction) {
      this.sortField = field
      this.sortDirection = direction
      sessionStorage.setItem(SORT_STATE_KEY, JSON.stringify({ field, direction }))
    },
    onStatusFilterChange(value) {
      sessionStorage.setItem(FILTER_STATE_KEY, value)
      this.currentPage = 1
    },
    clearAll() {
      this.statusFilter = ''
      this.query = ''
      sessionStorage.removeItem(FILTER_STATE_KEY)
      sessionStorage.removeItem(SEARCH_STATE_KEY)
      this.currentPage = 1
      this.performSearch('')
    },
    performSearch: debounce(function (query) {
        if (this.query !== query) {
            this.query = query;
            this.mealplans = [];
        }
        sessionStorage.setItem(SEARCH_STATE_KEY, query)
        if (!query.length) {
            db.collection("meal_plans").orderBy("created_at", "desc").get().then(snapshot => {
              this.mealplans = snapshot.docs.map(docSnapshot => {
                let doc = docSnapshot.data();
                doc.id = docSnapshot.id;
                return doc;
              });
            });
            return;
        }

        let searchParameters = {
          'q'         : query,
          'preset'    : 'admin_meal_plan_search',
          'sort_by': '_text_match:desc',
          'per_page': '100'
        };

        let that = this;
        search.collections('meal_plans')
          .documents()
          .search(searchParameters)
          .then(function (searchResults) {
            that.mealplans = searchResults.hits.map((hit) => hit.document );
          })
          .finally(() => {
            that.isLoading = false;
          })
      }, 500),
    isDesktop() {
      if (screen.width <= 760) {
        return false;
      } else {
        return true;
      }
    },
    createNewMealPlan() {
      db.collection("meal_plans").add({
          created_at: Date.now(),
          title: "New meal plan",
          tips: [],
          recipes: {},
          images: [],
          status: "draft",
          date: null,
          date_string: DateFormatter.displayDate(null),
          tags: [],
          seasonal_availability: { year_round: true, months: [] }
      })
      .then((docRef) => {
          console.log("Document written with ID: ", docRef.id);
          this.$router.push({ name: 'MealPlanView', params: { id: docRef.id } })
      })
      .catch((error) => {
          console.error("Error adding document: ", error);
      });
    },
    openMealPlan(mealplan) {
      this.$router.push({ name: 'MealPlanView', params: { id: mealplan.id } })
    },
    deleteMealPlan(mealplan) {
        console.log(mealplan)
        this.$buefy.dialog.confirm({
            title: 'Deleting mealplan',
            message: `Are you sure you want to delete mealplan: <b><i>"${mealplan.title}"</i></b>? <br /><br />This action cannot be undone.`,
            confirmText: 'Delete Meal Plan',
            type: 'is-danger',
            hasIcon: true,
            onConfirm: () => {
              db.collection("meal_plans").doc(mealplan.id)
              .delete()
              .then(() => {
                this.$buefy.toast.open({type: 'is-success', message: `Meal plan successfully deleted!`});
                this.performSearch(this.query);
              }).catch((error) => {
                this.$buefy.toast.open({type: 'is-danger', message: `Error deleting meal plan: ${error}`})
              });
            }
        })
    },
    duplicateMealPlan(mealplan) {
      db.collection("meal_plans").doc(mealplan.id)
        .get()
        .then((doc) => {
          if (doc.exists && doc.data()) {
            let newData = doc.data();
            newData['created_at'] = Date.now();
            newData['status'] = "draft";
            return db.collection("meal_plans").add(newData)
              .then((docRef) => {
                this.$buefy.toast.open({type: 'is-success', message: `Meal plan successfully duplicated! ${docRef.id}`});
                this.performSearch(this.query);
              });
          }
        })
        .catch((error) => {
          this.$buefy.toast.open({type: 'is-danger', message: `Error duplicating meal plan ${mealplan.id}: ${error}`})
        });
    },
    // PRESERVED: Meal plan color bars - critical for brand personality
    rowStyle(mealplan) {
      if (mealplan.backgroundColour == null || mealplan.highlightColour == null) { return null; }

      return {
        backgroundColor: mealplan.backgroundColour.colour,
        borderRight: `30px solid ${mealplan.highlightColour.colour}`,
        color: (mealplan.backgroundColour.invertTheme == "dark") ? Colours.black.colour : Colours.white.colour
      }
    },
    rowImageSrc(mealplan) {
      if (mealplan.images == null){
        return null;
      }
      return (mealplan.images.length != 0) ? mealplan.images[0].src : null;
    },
    formatDate(dateTime) {
      return DateFormatter.displayDate(dateTime);
    }
  }
}
</script>

<style scoped>
.search-wrapper {
  flex: 1;
  max-width: 400px;
  position: relative;
}

.search-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: var(--text-muted);
}

.search-wrapper .input {
  padding-left: 40px !important;
}

.thumbnail {
  height: 100% !important;
  object-fit: cover;
  border-radius: var(--radius-sm);
}

/* Meal plan title with color bar styling */
.meal-plan-title {
  padding: 8px 12px;
  font-weight: 600;
  border-radius: var(--radius-sm);
}

.date-badge {
  display: inline-block;
  padding: 4px 10px;
  background: var(--feeds-green-light);
  color: var(--feeds-green);
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 500;
}

.date-tag {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Recipes column */
.recipes-preview {
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: default;
}

.recipe-name {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
  line-height: 1.4;
}

/* Recipe hover panel */
.recipe-hover-panel {
  position: absolute;
  z-index: 100;
  background: white;
  border-radius: var(--radius-md);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: 12px;
  min-width: 260px;
  max-width: 320px;
}

.recipe-hover-header {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted);
  margin-bottom: 8px;
}

.recipe-hover-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}

.recipe-hover-item + .recipe-hover-item {
  border-top: 1px solid var(--border-light, #f0f0f0);
}

.recipe-hover-image {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-sm);
  object-fit: cover;
  flex-shrink: 0;
}

.recipe-hover-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.3;
}

.recipe-hover-empty {
  font-size: 13px;
  color: var(--text-muted);
  padding: 8px 0;
}
</style>
